import {
  PrimaryBigButton,
  SecondaryBigButton
} from 'components/ReusableComponents/Button/BigButtons'
import EditableHotelList from 'components/ReusableComponents/EditableHotelList'
import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import FormGroup from 'components/ReusableComponents/ManageHotel/FormGroup'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router'
import contractApi from 'services/api/contracts'
import { emailRegex, formatPhoneNumber, phoneValRegex } from 'utils/helpers'

import hotelSetupApi from '../../../../services/api/hotelSetup'
import ContactHistoryDialog from './ContactHistoryDialog'
import ContactTile from './ContactTile'
import styles from './EditGroupContract.module.scss'

const EditMerchantContract = () => {
  const { merchantId, key } = useParams()

  const [step, setStep] = useState('merchantContacts')
  const [isLoading, setIsLoading] = useState(true)
  const [lastUpdatedOn, setLastUpdatedOn] = useState('')

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [merchantContact, setMerchantContact] = useState()
  const [contactHistories, setContactHistories] = useState([])
  const [isAddingNewMerchantContact, setIsAddingNewMerchantContact] = useState(false)
  const [isContactHistoryDialogVisible, setIsContactHistoryDialogVisible] =
    useState(false)

  const [hotelsToIntegrate, setHotelsToIntegrate] = useState([])
  const [integratedHotels, setIntegratedHotels] = useState([])

  const [merchantName, setMerchantName] = useState('')

  const [hotelSetupStatuses, setHotelSetupStatuses] = useState()

  const history = useHistory()

  const getData = React.useCallback(() => {
    setIsLoading(true)

    Promise.all([
      contractApi.internalAdmin
        .get({ merchantId, key })
        .then(response => {
          const { updatedOn, merchantName, contacts, contactHistories, hotels } = response
          setLastUpdatedOn(updatedOn)
          setMerchantName(merchantName)
          setMerchantContact(contacts[0])
          setContactHistories(contactHistories)

          if (contacts.length > 0) {
            setStep('hotels')
          }

          setHotelsToIntegrate(hotels.filter(h => !h.isPreOnboardingCompleted))
          setIntegratedHotels(hotels.filter(h => h.isPreOnboardingCompleted))
        })
        .catch(console.warn),
      hotelSetupApi
        .steps({ isWholeGroup: true, merchantId })
        .then(res => {
          setHotelSetupStatuses(res.lastSteps)
        })
        .catch(console.warn)
    ]).finally(() => {
      setIsLoading(false)
    })
  }, [key, merchantId])

  useEffect(() => {
    getData()
  }, [getData])

  function handleSave() {
    setIsLoading(true)

    if (step === 'merchantContacts' || isAddingNewMerchantContact) {
      contractApi.internalAdmin
        .addNewMerchantContact({
          merchantId,
          firstName,
          lastName,
          email,
          phone: phone.replace(/\D/g, '')
        })
        .then(res => {
          setLastUpdatedOn(res.lastUpdatedOn)
          getData()
        })
        .catch(error => {
          console.warn(error)
        })
        .finally(() => {
          setIsLoading(false)
        })
    } else {
      getData()
    }
  }

  return (
    <div className={styles.wrapper}>
      {isContactHistoryDialogVisible && (
        <ContactHistoryDialog
          contacts={[
            {
              ...merchantContact,
              isActive: true
            },
            ...contactHistories
          ]}
          handleClose={() => setIsContactHistoryDialogVisible(false)}
        />
      )}
      {isLoading && <LoadingIndicator />}
      <CustomHeader title={`${merchantName} contract`} width={830} isFixed />
      <FormGroup title="Group contact" disabled={isLoading}>
        {merchantContact && !isAddingNewMerchantContact ? (
          <ContactTile
            name={merchantContact.firstName + ' ' + merchantContact.lastName}
            email={merchantContact.email}
            phone={formatPhoneNumber(merchantContact.phone)}
            onClickHistory={() => setIsContactHistoryDialogVisible(true)}
            onClickAdd={() => {
              setIsAddingNewMerchantContact(true)
              setMerchantContact(null)
              setFirstName('')
              setLastName('')
              setEmail('')
              setPhone('')
            }}
          />
        ) : (
          <div className={styles.formHolder}>
            <div className={styles.row}>
              <TextInput
                className={styles.halfInput}
                label="First name"
                value={firstName}
                onChangeValue={setFirstName}
              />
              <TextInput
                className={styles.halfInput}
                label="Last name"
                value={lastName}
                onChangeValue={setLastName}
              />
            </div>
            <div className={styles.row}>
              <TextInput
                className={styles.halfInput}
                label="Email"
                value={email}
                onChangeValue={setEmail}
                pattern={emailRegex}
              />
              <TextInput
                className={styles.halfInput}
                label="Mobile phone"
                value={phone}
                onChangeValue={value => setPhone(formatPhoneNumber(value))}
                pattern={phoneValRegex}
              />
            </div>
          </div>
        )}
      </FormGroup>
      <FormGroup
        title="Hotels to integrate"
        contentPadding="0"
        key={1}
        disabled={step === 'merchantContacts' || isLoading}
      >
        <EditableHotelList
          initialHotels={hotelsToIntegrate}
          onHotelAdded={getData}
          onChange={setHotelsToIntegrate}
          isEditable
          merchantId={merchantId}
          showUsers
          onNewUserAdded={getData}
          showStepInfo={true}
          hotelSetupStatuses={hotelSetupStatuses}
        />
      </FormGroup>
      {integratedHotels.length > 0 && (
        <FormGroup
          title="Integrated hotels"
          contentPadding="0"
          key={2}
          disabled={step === 'merchantContacts'}
        >
          <EditableHotelList
            initialHotels={integratedHotels}
            isEditable={false}
            canAddUsers={false}
          />
        </FormGroup>
      )}
      <div className={styles.bottomBar}>
        <div className={styles.bottomBarContainer}>
          <p className={styles.lastSavedText}>
            Last saved on {moment(lastUpdatedOn).format('MMM DD, YYYY, hh:MM A')}
          </p>
          <div className={styles.bottomBarButtonsHolder}>
            <SecondaryBigButton onClick={() => history.push('/contracts/list')}>
              Cancel
            </SecondaryBigButton>
            <PrimaryBigButton onClick={() => handleSave()}>Save changes</PrimaryBigButton>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EditMerchantContract
